/**
 * Everything the atlas renders came out of a workbook someone else prepared, so
 * an entity name, a category name and an indicator name are all attacker-supplied
 * by any reasonable reading. This is how they become markup safely.
 *
 * The first attempt escaped the whole snapshot at the boundary, in one place
 * instead of forty. It closed the hole, and it broke the atlas quietly: these
 * strings are not only shown, they are also keys. A category name goes into a
 * `data-n` attribute, the browser decodes the entity when it parses the
 * attribute, and the name that comes back no longer matches the key it was
 * stored under — so search offered "Funding, budget & investments" and clicking
 * it opened all of Ecosystem. Escaped data has to stay out of the model.
 *
 * So the model holds the real strings, and markup is built with the `h` tag,
 * which escapes every interpolation unless it is already trusted HTML. A test
 * checks that nothing assigns innerHTML any other way — that is what makes this
 * as complete as escaping at the boundary was.
 */

/** Marks a string as HTML that is already safe to insert. */
class SafeHtml {
  constructor(html) { this.html = html }
  toString() { return this.html }
}

export const esc = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** Trust a string that is markup we built ourselves — never a value from data. */
export const raw = (html) => new SafeHtml(html);

const render = (v) => {
  if (v instanceof SafeHtml) return v.html;
  if (Array.isArray(v)) return v.map(render).join('');
  if (v == null) return '';
  return esc(v);
};

/**
 * Tagged template for markup: h`<p>${name}</p>`.
 *
 * Interpolate anything — a value from the data is escaped, an `h` result or a
 * `raw()` string is inserted as-is, and an array is joined. Attribute values
 * must be quoted, as they already are throughout: h`<b data-n="${name}">`.
 */
export function h(strings, ...values) {
  let out = strings[0];
  for (let i = 0; i < values.length; i++) out += render(values[i]) + strings[i + 1];
  return new SafeHtml(out);
}
